<script lang="ts" setup>
    import ProductCard from "@/components/ProductCard.vue";
    import productVariantSwatches from "@/helpers/composables/productVariantSwatches.ts";
    import VueHorizontal from "vue-horizontal";
    import RothcoCta from "@/components/RothcoCta.vue";
    import SearchSpringApi from "@/helpers/api/SearchSpringAPI.ts";
    import { useCampaign, useSearchByTerm, useBoost } from "@/helpers/composables/searchSpringApi";
    import { useElementVisibility } from "@vueuse/core";
    import { ref, toRefs, onMounted, onBeforeUnmount, computed, watch, inject} from "vue";
    import type { PropType } from "vue";
    // import { route } from "ziggy-js";
    import type { Cta } from "@/helpers/builder-init/index";
    import type { Text } from "@/helpers/interfaces/BuilderFields.ts";
    import { useFavoritesStore } from "@/store/favorites";
    import { storeToRefs } from "pinia";
    import { watchDeep } from "@vueuse/core";
    import { usePage } from "@inertiajs/vue3";
    import { useProductDetailStore } from "@/store/productDetailStore";
    import { useSettingsStore } from "@/store/settings";
    import { attemptServerPrefetch } from "@/helpers/composables/attemptServerPrefetch";
    import _ from "lodash";

    import useGoogleManager from "@/helpers/composables/google";

    let props = defineProps({
        featured: {
            type: Boolean,
            default: false
        },
        eyebrow: {
            type: Object as PropType<Text>,
            default() {
                return {};
            }
        },
        heading: {
            type: Object as PropType<Text>,
            default() {
                return {};
            }
        },
        ariaLabel: {
            type: String,
            default: "Shop Products"
        },
        tag: {
            type: String,
            default: ""
        },
        boostTag: {
            type: [Array, String],
            default(){
                return []
            }
        },  
        cartProducts: {
            type: Array<String>,
            default(){
                return []
            }
        },
        autoplay: {
            type: [String, Number],
            default: 0
        },

        carouselClasses: {
            type: Object,
            default() {
                return {
                    section: "product-carousel",
                    body: "carousel-body",
                    item: "product-card"
                };
            }
        },
        showNavButtons: {
            type: Boolean,
            default: true
        },
        boostCategories: {
            type: Array,
            default(){
                return []
            }
        },
        togglePadding: {
            type: Boolean,
            default: false
        },
        allowQuickView: {
            type: Boolean,
            default: true
        },
        expandedProductCard: {
            type: Boolean,
            default: false
        },
        isNew: {
            type: Boolean,
            default: false
        },
        isBestSellers: {
            type: Boolean,
            default: false
        },
    });
    let route = inject("route");

    let settings = useSettingsStore();
    let { user, is_dealer} = storeToRefs(settings);
    const carouselRef = ref();
    const searchSpringApi = new SearchSpringApi();
    const searchByTermsSearchSpringAPi = new SearchSpringApi();
    let categoryFilter = computed(()=>{
        let category = usePage().props.category;
        if (category) {
            let filter = `ss[ss_category_hierarchy]=`;
            let categoryHeirarchy = `${category.category_name}`;
            while(category.parent){
                category = category.parent;
                categoryHeirarchy = `${category.category_name} > ${categoryHeirarchy}`;
            }
            return `${filter}${categoryHeirarchy}`;
        }
        return false;
    })

    let ssCategoryFilter = computed(()=>{
        let category = usePage().props.category;
        if (category && !props.tag) {
            let categoryHeirarchy = `${category.category_name}`;
            while(category.parent){
                category = category.parent;
                categoryHeirarchy = `${category.category_name} > ${categoryHeirarchy}`;
            }
            return [
               {
                field: 'ss_category_hierarchy',
                value: categoryHeirarchy
               },
               {
                field: 'advertising_restricted',
                value: 0
               }
            ];
        }
        return [
            {
                field: 'advertising_restricted',
                value: 0
            }
        ];
    })
    let { tag, boostTag, isNew, isBestSellers } = toRefs(props);
    
    let { data } = useCampaign({ tag, searchSpringApi, new_arrivals: isNew, is_best_sellers: isBestSellers, filters: ssCategoryFilter });
    let boostTags = computed(()=>{
        let tags = boostTag.value;
        if(!Array.isArray(tags)){
            tags = [tags];
        }

        // remove empty tags
        tags = tags.filter(tag => tag != undefined && tag != null && tag != '');
        return tags;
    })

    let boostResponse;
    if(boostTags.value.length > 0){
        if(props.cartProducts && props.cartProducts.length > 0){
            boostResponse = useBoost({ tags: boostTags, products:props.cartProducts, cart: props.cartProducts, shopperId: user.value.user_id ?? undefined, filters: ssCategoryFilter }).response;

        }else{
            let store = useProductDetailStore();
            let { productCode: productLineCode, categoryCodes} = storeToRefs(store);
            boostResponse = useBoost({ tags: boostTags, product: productLineCode, categories: categoryCodes.value ?? props.boostCategories, shopperId: user.value.user_id ?? undefined, filters: ssCategoryFilter }).response;
        }
    }else{
        boostResponse = ref([]);
    }

    const shouldUseSearchByTerm = computed(()=>(boostTags.value.length == 0 && !tag.value && !isNew.value) );

    let { data: searchSpringData, handleFilter } = shouldUseSearchByTerm.value 
        ? useSearchByTerm({ searchSpringApi: searchByTermsSearchSpringAPi }) : { data: ref([]), handleFilter: ()=>{} };

    const boostedData = computed(()=>{
        if(boostResponse.value.length > 0){
            let boostedBestSellers = boostResponse.value.length > 0 ? boostResponse.value[0].results.filter((product) => product.attributes.best_seller_rank !== null && product.attributes.best_seller_rank !== undefined) : [];
            boostedBestSellers.sort((a, b) => a.attributes.best_seller_rank - b.attributes.best_seller_rank);
            let topTenPercentCount = Math.ceil(boostedBestSellers.length * 0.1);
            boostedBestSellers = boostedBestSellers.slice(0, topTenPercentCount);
            return boostResponse.value.length > 0 ? boostResponse.value[0].results.map(result =>{
                let variants =  productVariants(result.attributes.variants)?.flat?.(1).map(variant => JSON.parse(variant)) ?? [];
                return {
                    numberOfColors: numberOfColors(variants),
                    productImages: [{ image: result.mappings.core.imageUrl }],
                    title: result.mappings.core.name,
                    url: route("productDetail.show", [result.mappings.core.url]),
                    price: result.mappings.core.price,
                    rating: result.mappings.core.rating,
                    reviewCount: result.mappings.core.ratingCount,
                    seo_name: result.mappings.core.url,
                    variants,
                    isFavorite: favoriteIds.value.find(item => item == result.mappings.core.uid) != undefined,
                    product_id: result.mappings.core.uid,
                    intellisuggestData: result.mappings.core.intellisuggestData,
                    intellisuggestSignature: result.mappings.core.intellisuggestSignature,
                    short_description: result?.attributes?.short_description ?? "",
                    bestSeller: boostedBestSellers.find(item => item.mappings.core.uid == result.mappings.core.uid) != undefined,
                }
            }) : [];
        }else{
            return [];
        }
    })
    
    let { productVariants, numberOfColors } = productVariantSwatches();
    let bestSellers = computed(()=>{
     
        // get the top 10% of products by best_seller_rank
        let products = searchSpringData.value.filter((product) => product.best_seller_rank !== null && product.best_seller_rank !== undefined);
        products.sort((a, b) => a.best_seller_rank - b.best_seller_rank);
        let topTenPercentCount = Math.ceil(products.length * 0.1);
        return products.slice(0, topTenPercentCount);
    })
   
    let products = computed(() => {
        if(boostTags.value.length > 0){
            return boostedData.value;
        }
        else if(tag.value){
            return data.value.results.map((product) => {
                return {
                    productImages: [{ image: product.imageUrl }],
                    title: product.name,
                    url: route("productDetail.show", [product.seo_name]),
                    price: product.price,
                    rating: product.rating,
                    reviewCount: product.ratingCount,
                    seo_name: product.seo_name,
                    variants: productVariants(product.variants),
                    numberOfColors: numberOfColors(productVariants(product.variants)),
                    isFavorite: favoriteIds.value.find(item => item == product.product_id) != undefined,
                    product_id: product.product_id,
                    intellisuggestData: product.intellisuggestData,
                    intellisuggestSignature: product.intellisuggestSignature,
                    short_description: product.description,
                    bestSellers: true,
                };
            });
        }
        else if(isNew.value){
            return data.value.results.map((product) => {
                return {
                    productImages: [{ image: product.imageUrl }],
                    title: product.name,
                    url: route("productDetail.show", [product.seo_name]),
                    price: product.price,
                    rating: product.rating,
                    reviewCount: product.ratingCount,
                    seo_name: product.seo_name,
                    variants: productVariants(product.variants),
                    numberOfColors: numberOfColors(productVariants(product.variants)),
                    isFavorite: favoriteIds.value.find(item => item == product.product_id) != undefined,
                    product_id: product.product_id,
                    intellisuggestData: product.intellisuggestData,
                    intellisuggestSignature: product.intellisuggestSignature,
                    short_description: product.description,
                };
            });
        }
        else{
            //bestSellers.value.find(item => item.product_id == product.product_id) != undefined,
            return searchSpringData.value.map((product) => {
                return {
                    productImages: [{ image: product.imageUrl }],
                    title: product.name,
                    url: route("productDetail.show", [product.seo_name]),
                    price: product.price,
                    rating: product.rating,
                    reviewCount: product.ratingCount,
                    seo_name: product.seo_name,
                    variants: productVariants(product.variants),
                    numberOfColors: numberOfColors(productVariants(product.variants)),
                    isFavorite: favoriteIds.value.find(item => item == product.product_id) != undefined,
                    product_id: product.product_id,
                    favorite_id: favorites.value?.find(favorite=>favorite.item_index == product.product_id)?.favorite_id,
                    intellisuggestData: product.intellisuggestData,
                    intellisuggestSignature: product.intellisuggestSignature,
                    bestSeller: props.isBestSellers,
                    short_description: product.short_description,
                };
            });
        }
        
    });

 

    let favoritesStore = useFavoritesStore();
    let { getFavorites } = favoritesStore;
    let { favorites } = storeToRefs(favoritesStore);
    let favoriteIds = computed(()=>{
        return favorites.value.map((favorite) => favorite.item_index);
    })
    

    function setFavorites(){
        if(shouldUseSearchByTerm.value && !isBestSellers.value){
            handleFilter(
                favoriteIds.value.map((id) => {
                    return{
                        facet: {
                            field: 'product_id',
                            multiple: true,
                        },
                        value: {
                            value: id
                        },
                    }
                })
            )
        }
        
    }

    onMounted(async() => {
        if(user.value.user_id){
            getFavorites();
        }

        setFavorites();

        watchDeep(favoriteIds, (newVal, oldVal) => {
            const hasInitialValues = newVal.length > 0 && oldVal === undefined;
            const hasDifferentValues = _.difference(newVal, (oldVal ?? [])).length > 0;
            if(hasInitialValues || hasDifferentValues){
                setFavorites();
            }
            
        }, {
            immediate: true
        });
    });
    watch(
        products,
        () => {
            carouselRef.value?.refresh();
        },
        { deep: true }
    );

    const targetIsVisible = useElementVisibility(carouselRef);
    const interval = ref();

    onBeforeUnmount(() => {
        clearInterval(interval.value);
    });

    watch(
        [targetIsVisible, () => props.autoplay],
        ([newIsVisible, newAutoplay]) => {
            if (!newIsVisible || !+newAutoplay) {
                clearInterval(interval.value);
            } else {
                clearInterval(interval.value);
                interval.value = setInterval(
                    play,
                    Math.max(+newAutoplay, 1000)
                ); //keep minimum of 1 second for interval time
            }
        },
        { immediate: true }
    );

    let play = () => {
        if (!hasNext.value && hasPrev.value) {
            carouselRef.value.scrollToIndex(0);
            return;
        }
        if (hasNext.value) {
            next(new MouseEvent("click")); //vue-horizontal's nav functions expect arg of a DOM event
        }
    };

    const onScroll = (event) => {
        // console.log('onScroll', event)
    };

    const onScrollDebounce = (event) => {
        // console.log('onScrollDebounce', event)
    };

    const scrollToIndex = (index)=>{
        carouselRef.value?.scrollToIndex(index);
    }

    const next = ($event) => {
        carouselRef.value.next($event);
    };
    const prev = ($event) => {
        carouselRef.value.prev($event);
    };
    const hasNext = computed(() => {
        return carouselRef?.value?.hasNext;
    });
    const hasPrev = computed(() => {
        return carouselRef?.value?.hasPrev;
    });


    let computedAriaLabel = computed(() => {
        if(!props.ariaLabel) {
            return "Shop Products";
        }
        return props.ariaLabel;
    });
    defineExpose({
        next,
        prev,
        hasNext,
        hasPrev,
        scrollToIndex
    });

    const getTextColorClass = (color) => `text-${color}`;


    let newArrivalsRoute = computed(()=>{
        let url = route('productListing.newArrivals');

        if(categoryFilter.value){
            url = `${url}?${categoryFilter.value}`;
        }
        return url;
    })

    let bestSellersRoute = computed(()=>{
        let url = route('productListing.bestSellers');

        if(categoryFilter.value){
            url = `${url}?${categoryFilter.value}`;
        }
        return url;
    })

    const shopAllRoute = computed(()=>{
        if(isBestSellers.value){
            return bestSellersRoute.value;
        }else if(isNew.value){
            return newArrivalsRoute.value;
        }else{
            return route('productListing.collection', [tag.value]);
        }
    })

    let {
        gtmViewItemListEvent
    } = useGoogleManager();

    watch(products, (newProducts)=>{
        if(newProducts.length > 0){
            let tagToUse = ''
            if(boostTags.value.length > 0){
                tagToUse = boostTags.value[0];
            }else if(tag.value){
                tagToUse = tag.value;
            }else if(isNew.value){
                let category = usePage().props.category;
                if(category){
                    tagToUse = `${category.category_name} New Arrivals`;
                }
                tagToUse = 'New Arrivals';
            }
            gtmViewItemListEvent(
                tagToUse, newProducts.map((product) => {
                    return {
                        item_name: product.title,
                        price: is_dealer.value ? undefined: product.price,
                    }
                })
            );
        }
    }, {
        immediate: true
    })
</script>
<template>
    <section :class="{
            [carouselClasses.section]: true,
            'alternative-padding': !props.togglePadding
        }"
        :aria-label="heading.text || 'Carousel'"
        role="region"
    >
        <div
            v-if="props.eyebrow.text"
            :class="['eyebrow', getTextColorClass(props.eyebrow.color)]"
        >
            {{ props.eyebrow.text }}
        </div>
        <span
            v-if="props.heading.text"
            :class="['headline-m', getTextColorClass(props.heading.color)]"
            role="heading"
            aria-level="2"
            >{{ props.heading.text }}</span
        >
        <RothcoCta
            v-if="tag && !featured || (isNew && !featured)"
            :href="shopAllRoute"
            cta-type="link"
            class="primary"
            :aria-label="computedAriaLabel"
        >
            Shop All
        </RothcoCta>
        <div
            v-show="carouselRef?.hasPrev"
            class="arrow-prev"
            @click="carouselRef.prev($event)"
            aria-label="Scroll left"
            role="button"
            tabindex="0"
        ></div>
        <vue-horizontal
            v-if="products?.length > 0"
            ref="carouselRef"
            responsive
            scroll
            :class="carouselClasses.body"
            @scroll="onScroll"
            @scroll-debounce="onScrollDebounce"
        >
            <ProductCard
                :ref="`product_card_${index}`"
                v-for="(product, index) in products"
                :key="index"
                :class="carouselClasses.item"
                :product-card-details="product"
                :allowQuickView="props.allowQuickView"
                :expanded="props.expandedProductCard"
            />
            <template v-slot:btn-next><span></span></template>
            <template v-slot:btn-prev><span></span></template>
        </vue-horizontal>
        <div
            v-show="carouselRef?.hasNext"
            class="arrow-next"
            @click="carouselRef.next($event)"
            aria-label="Scroll right"
            role="button"
            tabindex="0"
        ></div>
    </section>
</template>
